/**
 * ★★ EVT-04 — CE QU'UN ÉCRAN A LE DROIT DE NOMMER SELON LE TENANT QUI LE LIT.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LE DÉFAUT QUE CE FICHIER EXISTE POUR EMPÊCHER
 * ────────────────────────────────────────────────────────────────────────────
 *
 * C'est la quatrième occurrence d'une même famille : la vitrine niait la
 * boutique, le scan niait le hors-ligne, son pied de page le niait encore, et
 * maintenant les écrans d'un tenant `evenement` parlent de théâtre, de saison
 * et d'abonnés à quelqu'un qui organise une fête d'entreprise.
 *
 * La cause n'est jamais l'inattention. Elle est qu'une même phrase est écrite
 * dans deux, cinq ou onze JSX différents : le jour où l'une devient
 * conditionnelle, les autres restent. La barre latérale du back-office en est
 * la démonstration — le mot « Théâtre » et la rubrique « SAISON » s'affichent
 * sur ONZE écrans, et c'est **un seul endroit** à corriger, pas onze.
 *
 * ★ **La parade est la même que pour `lib/placement.ts` (le vocabulaire de la
 * place) et `lib/facture.ts` (les promesses de règlement) : UNE fonction porte
 * la phrase et prend la donnée en argument.** Ici la donnée est le type de
 * tenant, qui vient de `GET /theatre/publique` (`useTheatre().type`), donc du
 * registre de plateforme — la base d'un théâtre ne sait pas ce qu'elle est.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * DEUX NIVEAUX, ET LA FRONTIÈRE ENTRE EUX
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  1. **Le lexique** (`lexique()`) — les NOMS du métier, dans les formes dont
 *     le français a besoin (élision comprise : « la saison » mais « l'édition »,
 *     « du théâtre » mais « de l'organisateur »). Un écran qui n'a besoin que
 *     d'un mot dans une phrase qui lui est propre le prend ici.
 *  2. **Les phrases** — les tournures écrites à plus d'un endroit, ou dont la
 *     structure entière change d'un tenant à l'autre. Celles-là ne peuvent pas
 *     être recomposées mot à mot sans redevenir deux copies.
 *
 * ⚠️ **Ce fichier ne décide jamais de MASQUER un écran** : ce sont deux
 * questions distinctes, et les mélanger produirait des écrans muets. Le retrait
 * d'un écran sans objet est porté par `components/navigation.ts`
 * (`masqueSiEvenement`) et par `components/EcranSansObjet.tsx`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI `client` EST LE DÉFAUT, TOUJOURS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un type inconnu, une requête en vol, une API muette : dans tous ces cas on
 * sert le vocabulaire complet. Le produit est né théâtre, ses clients sont des
 * théâtres, et un mot de trop dans un menu coûte infiniment moins cher qu'un
 * écran amputé par un accident de réseau. C'est la même règle de sens que
 * `useTheatre().type`, qui vaut `client` tant que rien n'a répondu.
 */

import { textePublic, type Langue, type TypeTenantPublic } from '@tb/shared'

import { useTheatre } from './theatre'

/* ========================================================================== */
/* 1. LE LEXIQUE                                                              */
/* ========================================================================== */

/**
 * Les noms du métier, accordés au tenant.
 *
 * ★ Les formes détaillées (article, élision, pluriel) sont énumérées plutôt que
 * calculées. Une fonction d'élision serait plus courte et se tromperait le jour
 * où l'on ajoutera un mot commençant par un `h` muet ; surtout, elle rendrait
 * illisible ce qui doit se relire d'un coup d'œil : la colonne de gauche est ce
 * que lit un théâtre, celle de droite ce que lit un organisateur.
 */
export interface Lexique {
  /* ------------------------------------------------- ce que le tenant EST -- */
  /**
   * ★ La CHOSE dont on configure l'identité, le logo et les textes imprimés :
   * « théâtre » · « événement ».
   *
   * Distinct d'`exploitant`, et la distinction n'est pas cosmétique. « Nom du
   * théâtre » devient « Nom de l'événement », jamais « Nom de l'organisateur » :
   * ce que l'on saisit là, c'est « Fête du personnel ». À l'inverse, c'est bien
   * l'organisateur — et non l'événement — qui encaisse, à qui l'on écrit et à
   * qui l'on a communiqué son adresse. Un seul mot pour les deux rôles
   * produirait une phrase fausse d'un côté ou de l'autre à chaque emploi.
   */
  readonly tenant: string
  /** « le théâtre » · « l’événement ». */
  readonly leTenant: string
  /** « du théâtre » · « de l’événement ». */
  readonly duTenant: string

  /* ------------------------------------------------ celui qui exploite ---- */
  /** « théâtre » · « organisateur ». */
  readonly exploitant: string
  /** « le théâtre » · « l’organisateur ». */
  readonly leExploitant: string
  /** « du théâtre » · « de l’organisateur ». */
  readonly duExploitant: string
  /** « au théâtre » · « à l’organisateur ». */
  readonly auExploitant: string

  /* --------------------------------------------- le regroupement de dates -- */
  /** « saison » · « édition ». */
  readonly saison: string
  /** « la saison » · « l’édition ». */
  readonly laSaison: string
  /** « de la saison » · « de l’édition ». */
  readonly deLaSaison: string
  /** Complément sans article : « de saison » · « d’édition ». */
  readonly deSaison: string
  /** « une saison » · « une édition ». */
  readonly uneSaison: string
  /** « saisons » · « éditions ». */
  readonly saisons: string

  /* ------------------------------------------------------ ce qui se joue -- */
  /** « spectacle » · « événement ». */
  readonly spectacle: string
  /** « un spectacle » · « un événement ». */
  readonly unSpectacle: string
  /** « le spectacle » · « l’événement ». */
  readonly leSpectacle: string
  /** Complément sans article : « de spectacle » · « d’événement ». */
  readonly deSpectacle: string
  /** « spectacles » · « événements ». */
  readonly spectacles: string
  /**
   * « Nouveau spectacle » · « Nouvel événement ».
   *
   * ★ Distinct de `spectacle` : « Nouveau » élide en « Nouvel » devant une
   * voyelle, et « spectacle »/« événement » ne s'accordent pas de la même
   * façon. Énuméré comme le reste du lexique (voir l'en-tête du fichier) —
   * pas de fonction d'élision qui se tromperait sur le prochain mot ajouté.
   */
  readonly nouveauSpectacle: string

  /* ------------------------------------------------- une date programmée -- */
  /** « représentation » · « date ». */
  readonly representation: string
  /** « une représentation » · « une date ». */
  readonly uneRepresentation: string
  /** « la représentation » · « la date ». */
  readonly laRepresentation: string
  /** « cette représentation » · « cette date ». */
  readonly cetteRepresentation: string
  /** « de la représentation » · « de la date ». */
  readonly deLaRepresentation: string
  /** « représentations » · « dates ». */
  readonly representations: string

  /* ------------------------------------------------------- ce qui est dû -- */
  /**
   * ★★ CE QUI PEUT RESTER À PAYER : « souscriptions et ventes » · « ventes ».
   *
   * Ce n'est PAS un renommage, c'est un RETRAIT : le produit définit lui-même
   * la souscription comme un abonnement, et un tenant `evenement` n'a ni
   * formule ni souscription (EVT-02). Écrire « souscriptions et ventes » à un
   * organisateur de fête d'entreprise nomme une moitié de catégorie qui
   * n'existe pas chez lui — il n'y a rien à rebaptiser, il y a un terme de
   * l'énumération à laisser tomber.
   *
   * Entré dans le lexique parce qu'il s'écrit désormais à TROIS endroits (la
   * légende du tableau des impayés, sa prose d'état vide, et la description du
   * montant dû du tableau de bord). Tant qu'il n'en avait qu'un, il n'avait
   * rien à gagner à déménager ; à trois, c'est deux copies qui finiront par ne
   * plus suivre — le mécanisme exact des six occurrences précédentes.
   */
  readonly souscriptionsEtVentes: string
}

const LEXIQUE_THEATRE: Lexique = {
  tenant: 'théâtre',
  leTenant: 'le théâtre',
  duTenant: 'du théâtre',

  exploitant: 'théâtre',
  leExploitant: 'le théâtre',
  duExploitant: 'du théâtre',
  auExploitant: 'au théâtre',

  saison: 'saison',
  laSaison: 'la saison',
  deLaSaison: 'de la saison',
  deSaison: 'de saison',
  uneSaison: 'une saison',
  saisons: 'saisons',

  spectacle: 'spectacle',
  unSpectacle: 'un spectacle',
  leSpectacle: 'le spectacle',
  deSpectacle: 'de spectacle',
  spectacles: 'spectacles',
  nouveauSpectacle: 'Nouveau spectacle',

  representation: 'représentation',
  uneRepresentation: 'une représentation',
  laRepresentation: 'la représentation',
  cetteRepresentation: 'cette représentation',
  deLaRepresentation: 'de la représentation',
  representations: 'représentations',

  souscriptionsEtVentes: 'souscriptions et ventes',
}

/**
 * ★ Les mots d'un organisateur d'événement ponctuel, et pourquoi ceux-là.
 *
 *  · **organisateur** — SPEC-BOUTIQUE §8 ter C : le tenant n'est pas une
 *    maison, c'est quelqu'un qui monte une fête. « Organisation » désignerait
 *    la structure, pas l'interlocuteur à qui l'on écrit ou qui encaisse.
 *  · **édition** — le regroupement existe encore dans la base (une soirée
 *    annuelle a bien des millésimes), mais ce n'est pas une saison : EVT-02 dit
 *    « pas de saison ». « Édition » nomme exactement la même chose sans
 *    promettre une programmation d'automne à printemps.
 *  · **événement** — le mot du domaine, celui du parcours d'inscription.
 *  · **date** — une représentation EST une occurrence datée. « Séance » sonne
 *    administratif et « soirée » serait faux pour un open-air de midi ; la date
 *    est ce qu'un organisateur choisit, annonce et contrôle à l'entrée.
 */
const LEXIQUE_EVENEMENT: Lexique = {
  tenant: 'événement',
  leTenant: 'l’événement',
  duTenant: 'de l’événement',

  exploitant: 'organisateur',
  leExploitant: 'l’organisateur',
  duExploitant: 'de l’organisateur',
  auExploitant: 'à l’organisateur',

  saison: 'édition',
  laSaison: 'l’édition',
  deLaSaison: 'de l’édition',
  deSaison: 'd’édition',
  uneSaison: 'une édition',
  saisons: 'éditions',

  spectacle: 'événement',
  unSpectacle: 'un événement',
  leSpectacle: 'l’événement',
  deSpectacle: 'd’événement',
  spectacles: 'événements',
  nouveauSpectacle: 'Nouvel événement',

  representation: 'date',
  uneRepresentation: 'une date',
  laRepresentation: 'la date',
  cetteRepresentation: 'cette date',
  deLaRepresentation: 'de la date',
  representations: 'dates',

  souscriptionsEtVentes: 'ventes',
}

/** `true` pour un tenant de type `evenement` (EVT-01), et pour lui seul. */
export function estEvenement(type: TypeTenantPublic | undefined): boolean {
  return type === 'evenement'
}

/**
 * Le lexique du tenant. `client` par défaut — voir l'en-tête : on n'ampute
 * jamais le vocabulaire par accident de réseau.
 */
export function lexique(
  type: TypeTenantPublic | undefined = 'client',
  langue: Langue = 'fr',
): Lexique {
  const origine = estEvenement(type) ? LEXIQUE_EVENEMENT : LEXIQUE_THEATRE
  if (langue === 'fr') return origine
  return Object.fromEntries(
    Object.entries(origine).map(([clef, mot]) => [clef, MOTS_TRADUITS[mot]?.[langue] ?? mot]),
  ) as unknown as Lexique
}

/**
 * Le lexique du tenant courant, pour un composant.
 *
 * `useTheatre()` partage sa requête (`GET /theatre/publique`, même clé
 * TanStack) : appeler ce hook dans dix composants d'un même écran ne coûte
 * qu'une seule requête réseau.
 */
export function useLexique(langue: Langue = 'fr'): Lexique {
  return lexique(useTheatre().type, langue)
}

/**
 * Le type du tenant courant, pour les rares écrans dont la STRUCTURE change et
 * pas seulement les mots — une carte d'abonné qu'un événement n'imprime jamais,
 * une colonne de chiffres qui ne peut être que zéro.
 *
 * ⚠️ À n'employer que là : partout où seuls les MOTS diffèrent, `useLexique()`
 * dit la même chose sans laisser à chaque écran le soin de décider.
 */
export function useTypeTenant(): TypeTenantPublic {
  return useTheatre().type
}

/** Un mot du lexique en tête de phrase ou d'intitulé : « Saison », « Édition ». */
export function capitaliser(mot: string): string {
  return mot.charAt(0).toUpperCase() + mot.slice(1)
}

/* ========================================================================== */
/* 2. LES PHRASES                                                             */
/* ========================================================================== */

/*
 * ★ N'entrent ici que les tournures qui ne se recomposent PAS mot à mot :
 * celles écrites à plus d'un endroit (une phrase recopiée est une phrase qu'on
 * oubliera de conditionner d'un côté), et celles dont la structure entière
 * change. Tout le reste se compose au point d'usage à partir du lexique — une
 * phrase qui n'existe qu'à un seul endroit n'a rien à gagner à déménager.
 */

/**
 * L'écran de configuration du tenant (CFG-01) : entrée de menu, titre de la
 * page, et le renvoi que lui fait l'écran d'apparence. Trois endroits — donc
 * une fonction.
 */
export function titreConfiguration(type: TypeTenantPublic | undefined): string {
  return estEvenement(type) ? 'Événement et lieu' : 'Théâtre et salle'
}

/**
 * L'infobulle de l'entrée « Apparence » de la barre latérale (PER-01).
 *
 * Elle ne vit qu'à un endroit, mais dans un `title` — la surface qu'un lecteur
 * d'écran annonce et que l'œil ne relit jamais. C'est exactement là que le
 * défaut s'était logé ; elle est donc nommée plutôt que composée.
 */
export function descriptionApparence(type: TypeTenantPublic | undefined): string {
  return estEvenement(type)
    ? 'Couleur, police, logo et textes de l’événement'
    : 'Couleur, police, logo et textes du théâtre'
}

/**
 * L'en-tête de l'espace personnel (POR-01) et la porte qui y mène depuis la
 * page d'accueil.
 *
 * ★ Un tenant `evenement` n'a pas d'abonné — il n'a même pas de portail : la
 * porte est retirée de son affiche (`PageAccueilTheatre`). La tournure neutre
 * ci-dessous couvre le cas où quelqu'un arrive quand même sur l'adresse, et
 * elle dit vrai dans les deux cas : c'est bien l'espace d'une personne.
 */
export function titreEspaceAbonne(
  type: TypeTenantPublic | undefined,
  langue: Langue = 'fr',
): string {
  return textePublic(estEvenement(type) ? 'Espace personnel' : 'Espace abonné', langue)
}

/**
 * L'invitation faite au personnel, en pied de la page de connexion du public.
 *
 * ★ « Vous travaillez au théâtre ? » ne se recompose pas mot à mot : on ne
 * travaille pas « à l'organisateur ». La question change de tournure, pas
 * seulement de nom — c'est précisément le genre de phrase pour laquelle ce
 * fichier a un second niveau.
 */
export function questionPersonnel(
  type: TypeTenantPublic | undefined,
  langue: Langue = 'fr',
): string {
  return textePublic(
    estEvenement(type) ? 'Vous faites partie de l’organisation ?' : 'Vous travaillez au théâtre ?',
    langue,
  )
}

const MOTS_TRADUITS: Readonly<Record<string, { de: string; en: string }>> = {
  théâtre: {
    de: 'Theater',
    en: 'theatre',
  },
  'le théâtre': {
    de: 'das Theater',
    en: 'the theatre',
  },
  'du théâtre': {
    de: 'des Theaters',
    en: 'of the theatre',
  },
  'au théâtre': {
    de: 'dem Theater',
    en: 'the theatre',
  },
  événement: {
    de: 'Veranstaltung',
    en: 'event',
  },
  'l’événement': {
    de: 'die Veranstaltung',
    en: 'the event',
  },
  'de l’événement': {
    de: 'der Veranstaltung',
    en: 'of the event',
  },
  organisateur: {
    de: 'Veranstalter',
    en: 'organiser',
  },
  'l’organisateur': {
    de: 'der Veranstalter',
    en: 'the organiser',
  },
  'de l’organisateur': {
    de: 'des Veranstalters',
    en: 'of the organiser',
  },
  'à l’organisateur': {
    de: 'dem Veranstalter',
    en: 'the organiser',
  },
  saison: {
    de: 'Spielzeit',
    en: 'season',
  },
  'la saison': {
    de: 'die Spielzeit',
    en: 'the season',
  },
  'de la saison': {
    de: 'der Spielzeit',
    en: 'of the season',
  },
  'de saison': {
    de: 'der Spielzeit',
    en: 'of the season',
  },
  'une saison': {
    de: 'eine Spielzeit',
    en: 'a season',
  },
  saisons: {
    de: 'Spielzeiten',
    en: 'seasons',
  },
  édition: {
    de: 'Ausgabe',
    en: 'edition',
  },
  'l’édition': {
    de: 'die Ausgabe',
    en: 'the edition',
  },
  'de l’édition': {
    de: 'der Ausgabe',
    en: 'of the edition',
  },
  'd’édition': {
    de: 'der Ausgabe',
    en: 'of the edition',
  },
  'une édition': {
    de: 'eine Ausgabe',
    en: 'an edition',
  },
  éditions: {
    de: 'Ausgaben',
    en: 'editions',
  },
  spectacle: {
    de: 'Vorstellung',
    en: 'show',
  },
  'un spectacle': {
    de: 'eine Vorstellung',
    en: 'a show',
  },
  'le spectacle': {
    de: 'die Vorstellung',
    en: 'the show',
  },
  'de spectacle': {
    de: 'der Vorstellung',
    en: 'of a show',
  },
  spectacles: {
    de: 'Vorstellungen',
    en: 'shows',
  },
  'Nouveau spectacle': {
    de: 'Neue Vorstellung',
    en: 'New show',
  },
  'un événement': {
    de: 'eine Veranstaltung',
    en: 'an event',
  },
  'le nouvel événement': {
    de: 'die neue Veranstaltung',
    en: 'the new event',
  },
  'd’événement': {
    de: 'der Veranstaltung',
    en: 'of an event',
  },
  événements: {
    de: 'Veranstaltungen',
    en: 'events',
  },
  'Nouvel événement': {
    de: 'Neue Veranstaltung',
    en: 'New event',
  },
  représentation: {
    de: 'Vorstellung',
    en: 'performance',
  },
  'une représentation': {
    de: 'eine Vorstellung',
    en: 'a performance',
  },
  'la représentation': {
    de: 'die Vorstellung',
    en: 'the performance',
  },
  'cette représentation': {
    de: 'diese Vorstellung',
    en: 'this performance',
  },
  'de la représentation': {
    de: 'der Vorstellung',
    en: 'of the performance',
  },
  représentations: {
    de: 'Vorstellungen',
    en: 'performances',
  },
  date: {
    de: 'Termin',
    en: 'date',
  },
  'une date': {
    de: 'ein Termin',
    en: 'a date',
  },
  'la date': {
    de: 'der Termin',
    en: 'the date',
  },
  'cette date': {
    de: 'dieser Termin',
    en: 'this date',
  },
  'de la date': {
    de: 'des Termins',
    en: 'of the date',
  },
  dates: {
    de: 'Termine',
    en: 'dates',
  },
  'souscriptions et ventes': {
    de: 'Abonnemente und Verkäufe',
    en: 'subscriptions and sales',
  },
  ventes: {
    de: 'Verkäufe',
    en: 'sales',
  },
}
